import React, { useState, useEffect, useRef } from 'react';
import clutch from '../assets/clutch.png';

const Review = () => {
  const [years, setYears] = useState(0);
  const [satisfaction, setSatisfaction] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [responseTime, setResponseTime] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            animateNumbers();
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  const animateNumbers = () => {
    // Animate years (0 to 10)
    animateValue(setYears, 0, 10, 2000);
    
    // Animate satisfaction (0 to 98)
    animateValue(setSatisfaction, 0, 98, 2000);
    
    // Animate completed (0 to 111900500)
    animateValue(setCompleted, 0, 111900500, 2500, true);
    
    // Animate response time (0 to 2)
    animateValue(setResponseTime, 0, 2, 1000);
  };

  const animateValue = (setValue, start, end, duration, formatNumber = false) => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const value = Math.floor(progress * (end - start) + start);
      
      if (formatNumber) {
        setValue(value.toLocaleString());
      } else {
        setValue(value);
      }
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  };

  return (
    <div className="bg-white py-4 sm:py-14 md:py-16 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-[#000300]">
            Trusted by Thousands of Financial Companies
          </h2>
          <p className="mt-3 md:mt-4 max-w-2xl mx-auto font-medium text-lg md:text-xl text-[#000300]">
            Our reputation speaks for itself
          </p>
        </div>

        <div className="bg-gray-50/90 rounded-md p-6 md:p-8" ref={statsRef}>
          {/* Medium screen layout (md) - vertical stack */}
          <div className="md:hidden lg:flex flex-col lg:flex-row items-center justify-between">
            {/* Clutch Rating */}
            <a 
              href="https://clutch.co/profile/red-key-solutions#summary" 
              target="_blank" 
              rel="noopener noreferrer"
            >
            <div className="flex flex-col items-center mb-8 lg:mb-0">
              <div className="flex items-center mb-4">
                <img 
                  src={clutch}
                  alt="Clutch Logo" 
                  className="h-7 sm:h-10 md:h-10 lg:h-10 mr-4"
                />
                <div>
                  <p className="text-sm text-[#000300]">Reviewed on</p>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-2 text-gray-900 font-bold text-sm">5/5</span>
                  </div>
                  <p className="text-sm text-gray-800 mt-1">31 Reviews</p>
                </div>
              </div>
            </div>
            </a>

            {/* Stats - 2x2 grid for medium screens */}
            <div className="grid grid-cols-2 gap-6 w-full lg:w-auto lg:grid-cols-4">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-[#2A2A2A]">{years}</p>
                <p className="text-sm text-gray-800">Years</p>
                <p className="text-xs text-gray-700">Proven Track Record</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-[#2A2A2A]">{satisfaction}%</p>
                <p className="text-sm text-gray-800">Customer</p>
                <p className="text-xs text-gray-700">Satisfaction</p>
              </div>
              <div className="text-center">
                <p className="text-xl md:text-2xl font-bold text-[#2A2A2A]">{completed}</p>
                <p className="text-sm text-gray-800">We Have</p>
                <p className="text-xs text-gray-700">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-[#2A2A2A]">{responseTime}</p>
                <p className="text-sm text-gray-800">Mins</p>
                <p className="text-xs text-gray-700">Average Response Time</p>
              </div>
            </div>
          </div>

          {/* Special layout for medium screens only (md) */}
          <div className="hidden md:block lg:hidden">
            <div className="flex flex-col space-y-8">
              {/* Rating at top for medium screens */}
                  <a 
                    href="https://clutch.co/profile/red-key-solutions#summary" 
                    target="_blank" 
                    rel="noopener noreferrer">
                      <div className="flex justify-center">
                        <div className="flex items-center">
                          <img 
                            src={clutch}
                            alt="Clutch Logo" 
                            className="h-10 mr-4"
                          />
                          <div>
                            <p className="text-sm text-[#000300]">Reviewed on</p>
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                              <span className="ml-2 text-gray-900 font-bold">5/5</span>
                            </div>
                            <p className="text-sm text-gray-800 mt-1">31 Reviews</p>
                          </div>
                        </div>
                      </div>
                 </a>

              {/* Stats in 2x2 grid below */}
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#2A2A2A]">{years}</p>
                  <p className="text-sm text-gray-800">Years</p>
                  <p className="text-xs text-gray-700">Proven Track Record</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#2A2A2A]">{satisfaction}%</p>
                  <p className="text-sm text-gray-800">Customer</p>
                  <p className="text-xs text-gray-700">Satisfaction</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#2A2A2A]">{completed}</p>
                  <p className="text-sm text-gray-800">We Have</p>
                  <p className="text-xs text-gray-700">Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#2A2A2A]">{responseTime}</p>
                  <p className="text-sm text-gray-800">Mins</p>
                  <p className="text-xs text-gray-700">Average Response Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;